import { API, Competition, Endpoints, Nullable, ProjectAPI } from "@brg/api";
import { bProfile, useProfile } from "@brg/auth";
import { STATUSES } from "_competition/const";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CompetitionDisableBtnStatus } from "shared/pages/opencallsPage/GetCompetitionItem/ActionsButtons";
import { getMaxLimitAttemptsPerUser } from "shared/helpers/getMaxLimitAttemptsPerUser";
import { CurrentCompetitionActions, CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";

export interface StatusesInfo {
    allowedMax?: Nullable<number>,
    allowedCurrent?: Nullable<number>,
    allowedMaxBriefs?: Nullable<number>,
    allowedCurrentBriefs?: Nullable<number>,
    maxAttemptsPerUser?: Nullable<number>,
    currentAttemptsPerUser?: Nullable<number>,
}

export interface AllowedData {
    allowedInfo?: CompetitionDisableBtnStatus[],
    allowedStatus?: StatusesInfo,
    isMaxAttemptsLimitLoading?: boolean,
}

const ALLOWED_STATUSES = [STATUSES.Open];

export const MAX_BRIEF_COUNT = 0;

export const useGetLimitSendCompetition = (competition: Competition): [AllowedData] => {
    const [statusesInfo, setStatusesInfo] = useState<StatusesInfo>();
    const [statuses, setStatuses] = useState<CompetitionDisableBtnStatus[]>([]);
    const [error, setError] = useState(false);
    const profile = useProfile();
    const dispatch = useDispatch();
    const { attemptsRefreshNonce, userAttempts } = useSelector(CurrentCompetitionSelector);

    const isBrief = useMemo(() => (competition?.isBriefCompetition) , [competition]);
    const isModer = useMemo(() => {
        if (profile && competition) {
          return (bProfile.isModer(profile) ||
            competition?.moderatorID?.includes(profile?.accountId))
        }
      }, [profile, competition])

    const isParticipant = useMemo(
        //@ts-ignore
        () => (competition && competition?.participantAccountIds?.includes(profile?.accountId)) 
    , [competition, profile])

    const isPendingParticipant = useMemo(
        //@ts-ignore
        () => (competition && competition?.pendingParticipantsIds?.includes(profile?.accountId)) 
    , [competition, profile])

    const maxAttemptsPerUser = useMemo(
        () => getMaxLimitAttemptsPerUser(competition),
        [competition],
    );
    const [isAttemptsCheckDone, setIsAttemptsCheckDone] = useState(false);

    const onSetStatuses = (status: CompetitionDisableBtnStatus) => {
        if (status !== CompetitionDisableBtnStatus.none) {
            setStatuses((prev) => [...prev.filter((s) => s !== status), status]);
        }
    }

    const onSetStatusesInfo = (statusesList: StatusesInfo) => {
        setStatusesInfo((prev) => ({ ...prev, ...statusesList }));
    }

    useEffect(() => {
        if (
            competition.maxParticipantsCount &&
            competition.maxParticipantsCount > 0 && 
            ALLOWED_STATUSES?.includes(competition?.status)
        ) {

            setStatusesInfo(
                { 
                    allowedMax: competition.maxParticipantsCount, 
                    allowedCurrent: competition?.participantsCount || 0
                }
            )

            if (competition?.participantsCount && 
                competition?.participantsCount >= competition.maxParticipantsCount 
            ) {
                onSetStatuses(CompetitionDisableBtnStatus.max_projects)
            }
 
        }  
        
        if (isBrief && !isModer) {
            if (profile) {
                API.request()
                .endpoint(Endpoints.competitions)
                .url('/api/Competition/GetUserBriefParticipantsCount')
                .get()
                .addQuery('accountId', profile?.accountId)
                .send()
                .then((r) => r.json())
                .then((briefCount) => {
                    onSetStatuses( 
                        briefCount > MAX_BRIEF_COUNT ? 
                            CompetitionDisableBtnStatus.max_briefs : 
                            isPendingParticipant ? 
                            CompetitionDisableBtnStatus.participant_pending :
                            CompetitionDisableBtnStatus.none
                    )

                    if (briefCount > MAX_BRIEF_COUNT) {
                        onSetStatusesInfo(
                            {
                                allowedMaxBriefs: MAX_BRIEF_COUNT + 1,
                                allowedCurrentBriefs: MAX_BRIEF_COUNT + 1
                            }
                        )
                    }
                })
                .catch((e) => setError(true))
            }
        } 
        
        if (isPendingParticipant) 
            onSetStatuses(CompetitionDisableBtnStatus.participant_pending)

      }, [
        competition, 
        isModer, 
        isBrief, 
        isParticipant, 
        isPendingParticipant, 
        profile,
    ])

    useEffect(() => {
        setIsAttemptsCheckDone(false);
    }, [maxAttemptsPerUser, competition?.competitionId, profile?.accountId]);

    useEffect(() => {
        if (!maxAttemptsPerUser || !profile?.accountId || !competition?.competitionId) {
            setIsAttemptsCheckDone(true);
            return;
        }

        let cancelled = false;

        const competitionId = competition.competitionId;
        const competitionTags = [
            ...(competition.tags || []),
            ...(competition.regularTags || []),
            ...(competition.requredTags || []),
        ].map((tag) => tag?.tagId).filter(Boolean) as string[];

        ProjectAPI.GetList({
            authorIds: [profile.accountId],
            projectFilter: 1,
            isContextIgnored: true,
            projectStatus: competition.acceptPrivatePublished ? [1, 7, 10] : [1, 7],
            onlyAuthors: false,
            onlyBooks: '',
            ...(competitionTags.length ? { tagIds: competitionTags } : {}),
            limit: 100,
        } as any)
            .then((r) => r.json())
            .then((pageModel: { data?: any[] }) => {
                if (cancelled) {
                    return;
                }

                const projects = pageModel?.data || [];
                const fromApi = projects.filter((p) => {
                    const inApproves = !!p?.competitionApproves?.find?.(
                        (item: { competitionId?: string }) => item.competitionId === competitionId,
                    );
                    const inPending = !!p?.pendingCompetitionIds?.includes?.(competitionId);
                    return inApproves || inPending;
                }).length;
                const optimisticCurrent = userAttempts?.competitionId === competitionId
                    ? userAttempts.current
                    : 0;
                const currentAttemptsPerUser = Math.max(fromApi, optimisticCurrent);

                onSetStatusesInfo({
                    maxAttemptsPerUser,
                    currentAttemptsPerUser,
                });
                dispatch(CurrentCompetitionActions.setUserAttempts({
                    competitionId,
                    current: currentAttemptsPerUser,
                    max: maxAttemptsPerUser,
                }));

                if (currentAttemptsPerUser >= maxAttemptsPerUser) {
                    onSetStatuses(CompetitionDisableBtnStatus.max_attempts_per_user);
                } else {
                    setStatuses((prev) => prev.filter((s) => s !== CompetitionDisableBtnStatus.max_attempts_per_user));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsAttemptsCheckDone(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        maxAttemptsPerUser,
        profile?.accountId,
        competition?.competitionId,
        competition?.acceptPrivatePublished,
        competition?.tags,
        competition?.regularTags,
        competition?.requredTags,
        attemptsRefreshNonce,
    ]);

    const isMaxAttemptsLimitLoading = !!maxAttemptsPerUser
        && !!profile?.accountId
        && !!competition?.competitionId
        && !isAttemptsCheckDone;

      return useMemo(
        () => ([{ allowedInfo: statuses, allowedStatus: statusesInfo, isMaxAttemptsLimitLoading }]),
        [statuses, statusesInfo, isMaxAttemptsLimitLoading],
      )
}
import { API, Competition, Endpoints, Nullable, ProjectAPI } from "@brg/api";
import { bProfile, useProfile } from "@brg/auth";
import { STATUSES } from "_competition/const";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CompetitionDisableBtnStatus } from "shared/pages/opencallsPage/GetCompetitionItem/ActionsButtons";
import { getMaxLimitAttemptsPerUser } from "shared/helpers/getMaxLimitAttemptsPerUser";
import { CurrentCompetitionActions, CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";

export interface StatusesInfo {
    allowedMax?: Nullable<number>,
    allowedCurrent?: Nullable<number>,
    allowedMaxBriefs?: Nullable<number>,
    allowedCurrentBriefs?: Nullable<number>,
    maxAttemptsPerUser?: Nullable<number>,
    currentAttemptsPerUser?: Nullable<number>,
}

export interface AllowedData {
    allowedInfo?: CompetitionDisableBtnStatus[],
    allowedStatus?: StatusesInfo,
    isMaxAttemptsLimitLoading?: boolean,
}

const ALLOWED_STATUSES = [STATUSES.Open];

export const MAX_BRIEF_COUNT = 0;

export const useGetLimitSendCompetition = (competition: Competition): [AllowedData] => {
    const [statusesInfo, setStatusesInfo] = useState<StatusesInfo>();
    const [statuses, setStatuses] = useState<CompetitionDisableBtnStatus[]>([]);
    const [error, setError] = useState(false);
    const profile = useProfile();
    const dispatch = useDispatch();
    const { attemptsRefreshNonce, userAttempts } = useSelector(CurrentCompetitionSelector);

    const isBrief = useMemo(() => (competition?.isBriefCompetition) , [competition]);
    const isModer = useMemo(() => {
        if (profile && competition) {
          return (bProfile.isModer(profile) ||
            competition?.moderatorID?.includes(profile?.accountId))
        }
      }, [profile, competition])

    const isParticipant = useMemo(
        //@ts-ignore
        () => (competition && competition?.participantAccountIds?.includes(profile?.accountId)) 
    , [competition, profile])

    const isPendingParticipant = useMemo(
        //@ts-ignore
        () => (competition && competition?.pendingParticipantsIds?.includes(profile?.accountId)) 
    , [competition, profile])

    const maxAttemptsPerUser = useMemo(
        () => getMaxLimitAttemptsPerUser(competition),
        [competition],
    );
    const [isAttemptsCheckDone, setIsAttemptsCheckDone] = useState(false);

    const onSetStatuses = (status: CompetitionDisableBtnStatus) => {
        if (status !== CompetitionDisableBtnStatus.none) {
            setStatuses((prev) => [...prev.filter((s) => s !== status), status]);
        }
    }

    const onSetStatusesInfo = (statusesList: StatusesInfo) => {
        setStatusesInfo((prev) => ({ ...prev, ...statusesList }));
    }

    useEffect(() => {
        if (
            competition.maxParticipantsCount &&
            competition.maxParticipantsCount > 0 && 
            ALLOWED_STATUSES?.includes(competition?.status)
        ) {

            setStatusesInfo(
                { 
                    allowedMax: competition.maxParticipantsCount, 
                    allowedCurrent: competition?.participantsCount || 0
                }
            )

            if (competition?.participantsCount && 
                competition?.participantsCount >= competition.maxParticipantsCount 
            ) {
                onSetStatuses(CompetitionDisableBtnStatus.max_projects)
            }
 
        }  
        
        if (isBrief && !isModer) {
            if (profile) {
                API.request()
                .endpoint(Endpoints.competitions)
                .url('/api/Competition/GetUserBriefParticipantsCount')
                .get()
                .addQuery('accountId', profile?.accountId)
                .send()
                .then((r) => r.json())
                .then((briefCount) => {
                    onSetStatuses( 
                        briefCount > MAX_BRIEF_COUNT ? 
                            CompetitionDisableBtnStatus.max_briefs : 
                            isPendingParticipant ? 
                            CompetitionDisableBtnStatus.participant_pending :
                            CompetitionDisableBtnStatus.none
                    )

                    if (briefCount > MAX_BRIEF_COUNT) {
                        onSetStatusesInfo(
                            {
                                allowedMaxBriefs: MAX_BRIEF_COUNT + 1,
                                allowedCurrentBriefs: MAX_BRIEF_COUNT + 1
                            }
                        )
                    }
                })
                .catch((e) => setError(true))
            }
        } 
        
        if (isPendingParticipant) 
            onSetStatuses(CompetitionDisableBtnStatus.participant_pending)

      }, [
        competition, 
        isModer, 
        isBrief, 
        isParticipant, 
        isPendingParticipant, 
        profile,
    ])

    useEffect(() => {
        setIsAttemptsCheckDone(false);
    }, [maxAttemptsPerUser, competition?.competitionId, profile?.accountId]);

    useEffect(() => {
        if (!maxAttemptsPerUser || !profile?.accountId || !competition?.competitionId) {
            setIsAttemptsCheckDone(true);
            return;
        }

        let cancelled = false;

        const competitionId = competition.competitionId;
        const competitionTags = [
            ...(competition.tags || []),
            ...(competition.regularTags || []),
            ...(competition.requredTags || []),
        ].map((tag) => tag?.tagId).filter(Boolean) as string[];

        ProjectAPI.GetList({
            authorIds: [profile.accountId],
            projectFilter: 1,
            isContextIgnored: true,
            projectStatus: competition.acceptPrivatePublished ? [1, 7, 10] : [1, 7],
            onlyAuthors: false,
            onlyBooks: '',
            ...(competitionTags.length ? { tagIds: competitionTags } : {}),
            limit: 100,
        } as any)
            .then((r) => r.json())
            .then((pageModel: { data?: any[] }) => {
                if (cancelled) {
                    return;
                }

                const projects = pageModel?.data || [];
                const fromApi = projects.filter((p) => {
                    const inApproves = !!p?.competitionApproves?.find?.(
                        (item: { competitionId?: string }) => item.competitionId === competitionId,
                    );
                    const inPending = !!p?.pendingCompetitionIds?.includes?.(competitionId);
                    return inApproves || inPending;
                }).length;
                const optimisticCurrent = userAttempts?.competitionId === competitionId
                    ? userAttempts.current
                    : 0;
                const currentAttemptsPerUser = Math.max(fromApi, optimisticCurrent);

                onSetStatusesInfo({
                    maxAttemptsPerUser,
                    currentAttemptsPerUser,
                });
                dispatch(CurrentCompetitionActions.setUserAttempts({
                    competitionId,
                    current: currentAttemptsPerUser,
                    max: maxAttemptsPerUser,
                }));

                if (currentAttemptsPerUser >= maxAttemptsPerUser) {
                    onSetStatuses(CompetitionDisableBtnStatus.max_attempts_per_user);
                } else {
                    setStatuses((prev) => prev.filter((s) => s !== CompetitionDisableBtnStatus.max_attempts_per_user));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsAttemptsCheckDone(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        maxAttemptsPerUser,
        profile?.accountId,
        competition?.competitionId,
        competition?.acceptPrivatePublished,
        competition?.tags,
        competition?.regularTags,
        competition?.requredTags,
        attemptsRefreshNonce,
    ]);

    const isMaxAttemptsLimitLoading = !!maxAttemptsPerUser
        && !!profile?.accountId
        && !!competition?.competitionId
        && !isAttemptsCheckDone;

      return useMemo(
        () => ([{ allowedInfo: statuses, allowedStatus: statusesInfo, isMaxAttemptsLimitLoading }]),
        [statuses, statusesInfo, isMaxAttemptsLimitLoading],
      )
}
